import React, { useEffect, useState } from 'react';
import { Text, Button, Card } from 'v3/components';
import { styles } from './styles';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LinkIcon from '@mui/icons-material/Link';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import Spinner from 'react-bootstrap/Spinner';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';

function LoadingOverlay({ label = 'Carregando' }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(255,255,255,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        borderRadius: 12,
        padding: 16,
      }}
      aria-busy="true"
      role="status"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Spinner animation="border" role="status" style={{ color: '#9aa4ae' }} />
        <Text type="body" style={{ color: '#9aa4ae', fontWeight: 600 }}>
          {label}
        </Text>
      </div>
    </div>
  );
}

export default function CreateChecklist({
  mode,
  setMode,
  TopContent,
  onBack,
  onConfirmPortal,
  confirmLabel = 'Confirmar',
  confirmDisabled = false,
  isMaintenance = false,
  maintenanceMode = 'single',
  setMaintenanceMode,
  maintenanceDays = 0,
  setMaintenanceDays,
  linkBlocked = false,
  linkBlockedReason,
  LinkBody,
  loading = false,
  loadingLabel = 'Carregando',
}) {
  const disableActions = !!loading;
  const [daysInput, setDaysInput] = useState(String(maintenanceDays ?? 0));

  useEffect(() => {
    setDaysInput(String(maintenanceDays ?? 0));
  }, [maintenanceDays]);

  function clampToZero(n) {
    if (Number.isNaN(n)) return 0;
    return Math.max(0, n);
  }

  function commitDaysValue(nextValue) {
    if (!setMaintenanceDays) return;
    const n = clampToZero(Number(nextValue));
    setMaintenanceDays(n);
    setDaysInput(String(n));
  }

  function Tabs() {
    return (
      <div style={styles.tabsWrap}>
        <button
          type="button"
          onClick={() => setMode('portal')}
          style={styles.tabBtn(mode === 'portal', false)}
          disabled={disableActions}
        >
          <PersonOutlineIcon style={{ fontSize: 16, color: '#111827' }} />
          Criar Checklist Avulso
        </button>

        <button
          type="button"
          onClick={() => {
            if (linkBlocked) return;
            setMode('link');
          }}
          style={{
            ...styles.tabBtn(mode === 'link', true),
            ...(linkBlocked ? { opacity: 0.6, cursor: 'not-allowed' } : null),
          }}
          disabled={disableActions || linkBlocked}
        >
          <LinkIcon style={{ fontSize: 16, color: '#111827' }} />
          Criar Checklist Público
        </button>
      </div>
    );
  }

  function PortalBody() {
    const canEditDays = maintenanceMode === 'recurring';

    return (
      <>
        {!isMaintenance ? (
          <div style={styles.bigBox}>
            <button
              type="button"
              onClick={() => {
                if (disableActions) return;
                if (onConfirmPortal) onConfirmPortal('create');
              }}
              disabled={disableActions}
              style={{
                ...styles.actionItem(false),
                marginTop: 12,
                opacity: disableActions ? 0.7 : 1,
                cursor: disableActions ? 'not-allowed' : 'pointer',
              }}
            >
              <div style={styles.actionIconBox('neutral')}>
                <AddBoxOutlinedIcon style={{ fontSize: 18, color: '#1C4B75' }} />
              </div>

              <div style={styles.contentCol}>
                <div style={styles.textsCol}>
                  <span style={styles.actionTitle}>Criar</span>
                  <span style={styles.actionDesc}>
                    Criar o checklist para responder depois
                  </span>
                </div>
              </div>
            </button>
          </div>
        ) : null}

        {isMaintenance ? (
          <Card style={styles.maintenanceCard}>
            <div style={styles.maintenanceTitleRow}>
              <div style={styles.maintenanceIconBox}>
                <AutorenewOutlinedIcon style={{ fontSize: 16, color: '#111827' }} />
              </div>
              <Text type="regular" weight={700} color="dark">
                Recorrência do checklist
              </Text>
            </div>

            <div style={styles.maintenanceDivider} />

            <div style={styles.maintenanceOptions}>
              <label style={styles.maintenanceOption}>
                <input
                  type="radio"
                  name="maintenance-mode"
                  checked={maintenanceMode === 'single'}
                  onChange={() => {
                    if (setMaintenanceMode) setMaintenanceMode('single');
                  }}
                />
                <div style={styles.maintenanceOptionText}>
                  <div style={styles.maintenanceOptionTitle}>Único</div>
                  <div style={styles.maintenanceOptionDesc}>
                    Não gerar novo checklist automaticamente
                  </div>
                </div>
              </label>

              <label style={styles.maintenanceOption}>
                <input
                  type="radio"
                  name="maintenance-mode"
                  checked={maintenanceMode === 'recurring'}
                  onChange={() => {
                    if (setMaintenanceMode) setMaintenanceMode('recurring');
                  }}
                />
                <div style={styles.maintenanceOptionText}>
                  <div style={styles.maintenanceOptionTitle}>Recorrente</div>
                  <div style={styles.maintenanceOptionDesc}>
                    Criar novo checklist ao vencer
                  </div>
                </div>
              </label>
            </div>

            <div style={{ ...styles.maintenanceRow, opacity: canEditDays ? 1 : 0.6 }}>
              <div style={styles.maintenanceLeftIcon}>
                <AccessTimeOutlinedIcon style={{ fontSize: 16, color: '#6B7280' }} />
              </div>

              <div style={styles.maintenanceRowTexts}>
                <div style={styles.maintenanceRowTitle}>Vence em</div>
                <div style={styles.maintenanceRowDesc}>
                  Dias até o checklist expirar
                </div>
              </div>

              <div style={styles.maintenanceRightControl}>
                <button
                  type="button"
                  onClick={() => {
                    if (!canEditDays) return;
                    commitDaysValue((Number(maintenanceDays || 0) - 1));
                  }}
                  style={styles.stepBtn}
                  aria-label="Diminuir dias"
                  disabled={!canEditDays}
                >
                  −
                </button>

                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={daysInput}
                  onChange={(e) => {
                    if (!canEditDays) return;
                    const onlyDigits = e.target.value.replace(/[^\d]/g, '');
                    setDaysInput(onlyDigits);
                  }}
                  onBlur={() => {
                    if (!canEditDays) return;
                    commitDaysValue(daysInput || 0);
                  }}
                  onKeyDown={(e) => {
                    if (!canEditDays) return;
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  style={styles.numberInput}
                  disabled={!canEditDays}
                />

                <button
                  type="button"
                  onClick={() => {
                    if (!canEditDays) return;
                    commitDaysValue((Number(maintenanceDays || 0) + 1));
                  }}
                  style={styles.stepBtn}
                  aria-label="Aumentar dias"
                  disabled={!canEditDays}
                >
                  +
                </button>

                <span style={{ fontSize: 12, color: '#6B7280' }}>dias</span>
              </div>
            </div>
          </Card>
        ) : null}

        <div style={styles.footer}>
          <Button
            variant="secondary"
            onClick={onBack}
            disabled={disableActions}
            style={{ minWidth: 140 }}
          >
            <Text type="regular" weight={500}>
              Cancelar
            </Text>
          </Button>

          {isMaintenance ? (
            <Button
              onClick={onConfirmPortal}
              disabled={confirmDisabled || disableActions}
              style={{ minWidth: 160 }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                {disableActions ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    role="status"
                    style={{ color: '#ffffff' }}
                  />
                ) : null}
                <Text type="regular" weight={500}>
                  {confirmLabel}
                </Text>
              </span>
            </Button>
          ) : null}
        </div>
      </>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Tabs />

      {TopContent ? (
        <div style={{ marginBottom: 14 }}>{TopContent}</div>
      ) : null}

      {linkBlocked && mode === 'portal' ? (
        <div style={styles.linkBlockedNote}>
          <Text type="label" color="gray">
            {linkBlockedReason || 'Não é possível gerar link para este checklist.'}
          </Text>
        </div>
      ) : null}

      {mode === 'portal' ? <PortalBody /> : LinkBody ? LinkBody : null}

      {loading ? <LoadingOverlay label={loadingLabel} /> : null}
    </div>
  );
}
